"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import Star from "lucide-react/dist/esm/icons/star";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useCreateReview } from "@/hooks/useAssetReviews";
import { RecommendedAssetsRail } from "@/components/asset-detail/RecommendedAssetsRail";
import type { RecommendedAsset } from "@/hooks/useRecommendedAssets";
import { toast } from "sonner";

interface RatingPromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assetId: string;
  assetTitle: string;
  /**
   * "You might also like" rows to show under the rating ask — this is the
   * post-download recommendation surface. Omit and the dialog is a plain rating
   * prompt (its original behaviour). When present, submitting or skipping the
   * rating keeps the dialog open so the buyer can browse the recommendations
   * instead of closing on them.
   */
  recommendations?: RecommendedAsset[];
}

/**
 * Star picker with keyboard support and accessible labelling.
 * - Tab moves focus through the 5 stars
 * - Arrow keys + 1-5 digit keys adjust rating
 * - Filled stars use the `--warning` token (brand amber). Unfilled
 *   stars use the foreground muted token instead of `/40` to clear
 *   the WCAG AA non-text contrast bar.
 */
function StarPicker({
  rating,
  onChange,
  firstButtonRef,
}: {
  rating: number;
  onChange: (r: number) => void;
  firstButtonRef?: React.RefObject<HTMLButtonElement | null>;
}) {
  const [hover, setHover] = useState(0);

  const handleKey = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, star: number) => {
      if (e.key === "ArrowRight" || e.key === "ArrowUp") {
        e.preventDefault();
        onChange(Math.min(5, (rating || star) + 1));
      } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
        e.preventDefault();
        onChange(Math.max(1, (rating || star) - 1));
      } else if (/^[1-5]$/.test(e.key)) {
        e.preventDefault();
        onChange(Number(e.key));
      }
    },
    [onChange, rating],
  );

  return (
    <div
      className="flex justify-center gap-1.5"
      role="radiogroup"
      aria-label="Rating, 1 to 5 stars"
    >
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = (hover || rating) >= star;
        return (
          <button
            key={star}
            ref={star === 1 ? firstButtonRef : undefined}
            type="button"
            role="radio"
            aria-checked={rating === star}
            aria-label={`${star} star${star > 1 ? "s" : ""}`}
            onClick={() => onChange(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            onKeyDown={(e) => handleKey(e, star)}
            className="rounded-md p-1 transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Star
              className={cn(
                "h-8 w-8 transition-colors",
                filled
                  ? "fill-warning text-warning"
                  : "fill-transparent text-muted-foreground",
              )}
              strokeWidth={1.75}
              aria-hidden="true"
            />
          </button>
        );
      })}
    </div>
  );
}

const RATING_LABELS: Record<number, string> = {
  1: "Not great",
  2: "Could be better",
  3: "It's okay",
  4: "Pretty good",
  5: "Love it",
};

export function RatingPromptDialog({
  open,
  onOpenChange,
  assetId,
  assetTitle,
  recommendations = [],
}: RatingPromptDialogProps) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  // Once the buyer has rated or skipped, collapse the rating controls but keep
  // the dialog open on the recommendations (when there are any).
  const [ratingResolved, setRatingResolved] = useState(false);
  const { mutateAsync: createReview, isPending } = useCreateReview();
  const firstStarRef = useRef<HTMLButtonElement | null>(null);

  const hasRecs = recommendations.length > 0;

  // Focus the first star on open so keyboard users can rate immediately;
  // reset form state on close so a re-open starts clean.
  useEffect(() => {
    if (!open) {
      setRating(0);
      setComment("");
      setRatingResolved(false);
      return;
    }
    const id = requestAnimationFrame(() => firstStarRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  // When the rating is resolved and there is nothing else to show, close.
  const resolveRating = () => {
    markPrompted(assetId);
    if (hasRecs) {
      setRatingResolved(true);
    } else {
      onOpenChange(false);
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) return;
    try {
      await createReview({
        assetId,
        rating,
        content: comment.trim() || undefined,
      });
      toast.success("Thanks — your rating helps other creators.");
      resolveRating();
    } catch {
      toast.error("Couldn't submit review. You may have already rated this asset.");
      onOpenChange(false);
    }
  };

  const handleSkip = () => {
    resolveRating();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "overflow-hidden p-0",
          hasRecs ? "sm:max-w-[560px]" : "sm:max-w-[400px]",
        )}
      >
        <div className="border-b border-border/60 bg-muted/30 px-5 py-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Star className="h-4 w-4 fill-warning text-warning" aria-hidden="true" />
              {ratingResolved ? "Nice grab!" : "Rate this asset"}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {ratingResolved ? (
                "Here's what pairs well with it."
              ) : (
                <span className="font-medium text-foreground">{assetTitle}</span>
              )}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="max-h-[70dvh] space-y-5 overflow-y-auto p-5">
          {!ratingResolved && (
            <>
              <div className="space-y-2 text-center">
                <StarPicker rating={rating} onChange={setRating} firstButtonRef={firstStarRef} />
                <p
                  className={cn(
                    "text-sm font-medium transition-opacity",
                    rating > 0 ? "text-foreground opacity-100" : "text-muted-foreground opacity-100",
                  )}
                  aria-live="polite"
                >
                  {rating > 0 ? RATING_LABELS[rating] : "Pick a rating"}
                </p>
              </div>

              {rating > 0 && (
                <div className="animate-in fade-in slide-in-from-bottom-1 duration-200">
                  <label htmlFor="rating-comment" className="sr-only">
                    Optional comment
                  </label>
                  <Textarea
                    id="rating-comment"
                    placeholder="Optional: what made it that rating?"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={2}
                    className="resize-none text-sm"
                    maxLength={300}
                  />
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={handleSkip}>
                  {hasRecs ? "Skip" : "Not now"}
                </Button>
                <Button
                  size="sm"
                  className="flex-1"
                  disabled={rating === 0 || isPending}
                  onClick={handleSubmit}
                >
                  {isPending ? "Submitting…" : "Submit rating"}
                </Button>
              </div>
            </>
          )}

          {hasRecs && (
            <RecommendedAssetsRail
              assets={recommendations}
              sourceAssetId={assetId}
              source="post_download"
              heading={ratingResolved ? "You might also like" : "While you're here"}
              columns={3}
              onNavigate={() => onOpenChange(false)}
              className={ratingResolved ? undefined : "border-t border-border/60 pt-5"}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function markPrompted(assetId: string) {
  try {
    const key = `rating_prompted_${assetId}`;
    localStorage.setItem(key, Date.now().toString());
  } catch {}
}

export function wasRecentlyPrompted(assetId: string, windowMs = 30 * 24 * 60 * 60 * 1000): boolean {
  try {
    const key = `rating_prompted_${assetId}`;
    const ts = localStorage.getItem(key);
    if (!ts) return false;
    return Date.now() - parseInt(ts, 10) < windowMs;
  } catch {
    return false;
  }
}
